import Link from "next/link";

import type { ProgramListItem } from "@/lib/api/api-client";
import { HeroCard } from "@/components/ui/molecules/hero-card";
import { getLevelStyle } from "@/components/learn/level-styles";
import { learnProgramPath } from "@/lib/learn/learn-routes";

/**
 * La sortie vers le parcours guidé, intercalée dans le catalogue.
 *
 * # Pourquoi elle existe
 *
 * Ouvrir deux cents morceaux quand on ne sait pas jouer, c'est voir deux cents
 * portes fermées. Une liste de morceaux n'est qu'une des deux réponses à « par
 * où commencer » ; l'autre est un parcours qui pose les mains avant de poser un
 * morceau, et c'est celle dont a besoin quelqu'un qui n'a jamais joué.
 *
 * # Pourquoi elle a la forme d'une carte de programme
 *
 * Un encadré gris avec un lien texte se lit comme un bandeau d'information,
 * c'est-à-dire comme quelque chose qu'on saute. Ici c'est le **vrai** programme
 * qui est montré, avec la carte que `/learn` utilise déjà, ses données réelles
 * et l'accent de son niveau : la promesse est vérifiable avant le clic, et
 * l'élément appartient à l'écran au lieu d'y être posé.
 *
 * # Pourquoi l'en-tête est celui d'une sélection
 *
 * Nom en 18 px puis phrase d'intention : c'est la grammaire des sections qui
 * l'entourent, mise à jour en même temps qu'elles. L'interstitiel se lit alors comme un voisin des
 * sélections et non comme une interruption, alors même que son contenu diffère.
 */
export function LearnCallout({ program }: { program: ProgramListItem }) {
  const style = getLevelStyle(program.level);

  return (
    <section className="flex flex-col gap-3">
      <header className="px-1">
        {/*
          Une amorce, pas un titre : le titre de cette section est celui de la
          carte (« Débuter au piano »), qui est déjà un `h2`. En mettre un
          second ici donnerait deux titres frères pour une seule section, là où
          les sélections qui l'entourent n'en ont qu'un. Le style, lui, est
          celui de leurs titres, ce qui suffit à faire voisin.
        */}
        <p className="text-[18px] font-medium leading-snug tracking-[-0.01em] text-text-heading">
          Tu n&apos;as jamais joué&nbsp;?
        </p>
        <p className="mt-1.5 max-w-[68ch] text-[13px] leading-snug text-text-soft">
          Choisir un morceau suppose de savoir déjà se repérer sur les touches. Le parcours, lui, prend les choses dans
          l&apos;ordre.
        </p>
      </header>

      <Link
        href={learnProgramPath(program.vpfId)}
        className="rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <HeroCard
          hue={style.hue}
          color={style.color}
          badge="Parcours guidé"
          title={program.title}
          description={program.goal}
          meta={[
            `${program.pieceCount} morceaux`,
            `${program.session.minutes} min par jour`,
          ]}
          action={{ label: "Commencer" }}
          density="compact"
        />
      </Link>
    </section>
  );
}
